import React, { useState } from 'react';
import { ChevronLeft, Send, Info } from 'lucide-react';
import { AssessmentType, ReachScore } from '../../../services/reachService';
import { REACHStageBadge } from '../common/REACHStageBadge';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const questions = require('../../../data/reach-assessment-questions.json');

type ScoreMap = Partial<Record<string, ReachScore>>;

interface Props {
  type: AssessmentType;
  projectId: string;
  onSubmit: (data: {
    respondentName: string;
    role: string;
    type: AssessmentType;
    individual: Record<string, ReachScore>;
    organisational: Record<string, ReachScore>;
  }) => Promise<void>;
  onBack: () => void;
}

const TYPE_LABELS: Record<AssessmentType, string> = {
  champion: 'Change Champion',
  executive: 'Executive Sponsor',
  'practice-leader': 'Practice Leader',
  user: 'End User',
  general: 'General',
};

const STAGES = ['R', 'E', 'A', 'C', 'H'] as const;

const STAGE_NAMES: Record<string, string> = {
  R: 'Recognise / Rally',
  E: 'Engage / Enable',
  A: 'Acquire / Architect',
  C: 'Capability / Cultivate',
  H: 'Habits / Hardwire',
};

const SCORE_META: { score: ReachScore; label: string; active: string; dot: string }[] = [
  { score: 1, label: 'Not yet',     active: 'bg-red-500 text-white border-red-500',     dot: 'bg-red-500' },
  { score: 2, label: 'Emerging',    active: 'bg-amber-400 text-white border-amber-400', dot: 'bg-amber-400' },
  { score: 3, label: 'Established', active: 'bg-blue-500 text-white border-blue-500',   dot: 'bg-blue-500' },
  { score: 4, label: 'Embedded',    active: 'bg-green-500 text-white border-green-500', dot: 'bg-green-500' },
];

const GuidanceCell: React.FC<{ text: string; guidance?: string }> = ({ text, guidance }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-1">
        <p className="text-xs text-navy leading-snug flex-1">{text}</p>
        {guidance && (
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            className={`flex-shrink-0 mt-0.5 transition-colors ${open ? 'text-primary' : 'text-slate-300 hover:text-slate-400'}`}
            title="Show guidance"
          >
            <Info size={13} />
          </button>
        )}
      </div>
      {open && guidance && (
        <p className="text-[11px] text-slate-500 bg-slate-50 rounded-lg px-2.5 py-2 border border-slate-100 leading-snug">
          {guidance}
        </p>
      )}
    </div>
  );
};

const CellScore: React.FC<{ value: ReachScore | undefined; onChange: (v: ReachScore) => void }> = ({ value, onChange }) => (
  <div className="flex gap-1 mt-2.5">
    {SCORE_META.map(({ score, active }) => (
      <button
        key={score}
        type="button"
        onClick={() => onChange(score)}
        className={`w-8 h-7 rounded text-xs font-bold transition-all border flex items-center justify-center ${
          value === score ? active : 'bg-slate-100 text-slate-400 border-transparent hover:bg-slate-200'
        }`}
      >
        {score}
      </button>
    ))}
  </div>
);

export const AssessmentForm: React.FC<Props> = ({ type, onSubmit, onBack }) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [individualScores, setIndividualScores] = useState<ScoreMap>({});
  const [orgScores, setOrgScores] = useState<ScoreMap>({});
  const [submitting, setSubmitting] = useState(false);

  const typeQuestions = questions[type] ?? questions['general'];

  const setScore = (id: string, v: ReachScore, layer: 'individual' | 'organisational') => {
    if (layer === 'individual') setIndividualScores(s => ({ ...s, [id]: v }));
    else setOrgScores(s => ({ ...s, [id]: v }));
  };

  const indQuestions: any[] = typeQuestions.individual ?? [];
  const orgQuestions: any[] = typeQuestions.organisational ?? [];

  // Index by stage for O(1) lookup
  const indByStage = Object.fromEntries(indQuestions.map((q: any) => [q.stage, q]));
  const orgByStage = Object.fromEntries(orgQuestions.map((q: any) => [q.stage, q]));

  const totalCells = STAGES.length * 2;
  const scored = STAGES.reduce((n, s) => {
    const iq = indByStage[s];
    const oq = orgByStage[s];
    return n + (iq && individualScores[iq.id] ? 1 : 0) + (oq && orgScores[oq.id] ? 1 : 0);
  }, 0);
  const allScored = scored === totalCells;

  // Per-column gap indicators (only shown once all scored)
  const gaps = allScored ? STAGES.map(s => {
    const iq = indByStage[s];
    const oq = orgByStage[s];
    const iv = iq ? (individualScores[iq.id] ?? 0) : 0;
    const ov = oq ? (orgScores[oq.id] ?? 0) : 0;
    return { stage: s, diff: Math.abs(iv - ov) };
  }) : [];

  const handleSubmit = async () => {
    if (!name.trim()) return;
    const toREACH = (byStage: Record<string, any>, scores: ScoreMap) => {
      const out: Record<string, ReachScore> = {};
      STAGES.forEach(s => {
        const q = byStage[s];
        if (q && scores[q.id]) out[s.toLowerCase()] = scores[q.id]!;
      });
      return out;
    };
    setSubmitting(true);
    try {
      await onSubmit({
        respondentName: name.trim(),
        role: role.trim(),
        type,
        individual: toREACH(indByStage, individualScores) as any,
        organisational: toREACH(orgByStage, orgScores) as any,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-400 hover:text-navy transition-colors">
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-base font-bold text-navy">{TYPE_LABELS[type]} Assessment</h2>
      </div>

      {/* Respondent info */}
      <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-navy mb-2">
            Respondent Name <span className="text-red-500">*</span>
          </label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Enter name"
            className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-navy mb-2">Role / Job Title</label>
          <input
            value={role}
            onChange={e => setRole(e.target.value)}
            placeholder="e.g. Change Manager"
            className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </div>
      </div>

      {/* Scoring matrix */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">

        {/* Score legend */}
        <div className="px-5 py-3 border-b border-[#e2e8f0] bg-slate-50/70 flex items-center gap-1 flex-wrap">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mr-3">Score key</span>
          {SCORE_META.map(({ score, label, dot }) => (
            <div key={score} className="flex items-center gap-1.5 mr-4">
              <span className={`w-5 h-5 rounded text-white text-xs font-bold flex items-center justify-center ${dot}`}>{score}</span>
              <span className="text-xs text-slate-500">{label}</span>
            </div>
          ))}
          <span className="ml-auto text-xs font-semibold text-slate-400">
            {scored}/{totalCells} scored
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[#e2e8f0]">
                <th className="w-[180px] px-4 py-3 text-left bg-slate-50/50" />
                {([
                  { layer: 'individual' as const, label: 'Individual' },
                  { layer: 'organisational' as const, label: 'Organisational' },
                ]).map(({ layer, label }, i) => (
                  <th
                    key={layer}
                    className={`px-4 py-3 text-center bg-white ${i > 0 ? 'border-l border-[#e2e8f0]' : ''}`}
                  >
                    <span className="text-[11px] font-bold text-primary tracking-widest uppercase">{label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {STAGES.map((stage, ri) => {
                const gap = gaps.find(g => g.stage === stage);
                const indQ = indByStage[stage];
                const orgQ = orgByStage[stage];
                return (
                  <tr key={stage} className={ri < STAGES.length - 1 ? 'border-b border-[#e2e8f0]' : ''}>
                    <td className="px-4 py-4 bg-slate-50/70 align-top">
                      <div className="flex flex-col gap-1.5">
                        <REACHStageBadge stage={stage as any} showLabel={false} size="sm" />
                        <p className="text-[11px] font-semibold text-slate-500 leading-tight">{STAGE_NAMES[stage]}</p>
                        {gap && gap.diff >= 2 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 w-fit">
                            gap ↕ {gap.diff}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      {indQ ? (
                        <>
                          <GuidanceCell text={indQ.text} guidance={indQ.guidance} />
                          <CellScore value={individualScores[indQ.id]} onChange={v => setScore(indQ.id, v, 'individual')} />
                        </>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-4 align-top border-l border-[#e2e8f0]">
                      {orgQ ? (
                        <>
                          <GuidanceCell text={orgQ.text} guidance={orgQ.guidance} />
                          <CellScore value={orgScores[orgQ.id]} onChange={v => setScore(orgQ.id, v, 'organisational')} />
                        </>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Submit row */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">
          {allScored ? 'All questions answered — ready to submit.' : (() => {
            const rem = totalCells - scored;
            return `${rem} question${rem !== 1 ? 's' : ''} remaining.`;
          })()}
        </p>
        <button
          onClick={handleSubmit}
          disabled={submitting || !name.trim() || !allScored}
          className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-40"
        >
          <Send size={15} /> {submitting ? 'Submitting…' : 'Submit Assessment'}
        </button>
      </div>
    </div>
  );
};
